import React from 'react';
import * as FiIcons from 'react-icons/fi';
const { FiCopy, FiTrash2, FiEdit3, FiClock, FiAlertCircle, FiStar, FiLock, FiEye, FiType, FiCheckSquare, FiSquare, FiCode } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import { htmlToPlainText, CLOUD_SYNC_SAFE_LIMIT } from '../utils/constants';

export default function SnippetCard({
  snippet,
  onCopy,
  onDelete,
  onEdit,
  onPreview,
  isPinned,
  onTogglePin,
  onTogglePermanent,
  isSelectionMode = false,
  isSelected = false,
  onSelect,
  usageCount = 0,
  viewMode = 'grid'
}) {
  const daysPassed = Math.floor((Date.now() - (snippet.lastUsedAt || snippet.createdAt)) / 86400000);
  const daysRemaining = Math.max(0, 30 - daysPassed);
  const isExpiringSoon = daysRemaining <= 5 && !snippet.isPermanent;
  const charCount = snippet?.content ? snippet.content.length : 0;
  const isCloudBlocked = charCount > CLOUD_SYNC_SAFE_LIMIT;
  const hasTokens = Boolean(snippet.content && /{{\s*([^}]+)\s*}}/.test(snippet.content));
  const isTemplate = snippet.type === 'template' || hasTokens;
  const plainPreview = snippet.type === 'html' ? htmlToPlainText(snippet.content) : snippet.content;

  const typeLabel = snippet.type === 'html' ? 'HTML' : snippet.type === 'template' ? 'TEMPLATE' : 'TEXT';
  const typeClass = snippet.type === 'html'
    ? 'bg-orange-500/10 text-orange-400 border-orange-500/25'
    : isTemplate
      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
      : 'bg-blue-500/10 text-blue-400 border-blue-500/25';

  const updatedLabel = (() => {
    const ts = Number(snippet.updatedAt || snippet.createdAt || 0);
    if (!ts) return '';
    const days = Math.floor((Date.now() - ts) / 86400000);
    if (days <= 0) return 'Today';
    if (days === 1) return '1d ago';
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks}w ago`;
    return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  })();

  return (
    <article
      className={`snippet-card group ${isSelected ? 'selected' : ''} ${viewMode === 'list' ? 'list-card' : ''}`}
      onDoubleClick={() => onPreview(snippet)}
    >
      {isSelectionMode && (
        <button
          onClick={() => onSelect?.(snippet.id)}
          className="absolute left-2.5 top-2.5 z-20 w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-400 border border-indigo-500/30"
          title="Select snippet"
        >
          <SafeIcon icon={isSelected ? FiCheckSquare : FiSquare} />
        </button>
      )}

      <div className={`px-3 pt-3 pb-1.5 flex items-center gap-2 min-w-0 ${isSelectionMode ? 'pl-12' : ''}`}>
        <span className={`shrink-0 text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-wide border ${typeClass}`}>
          {typeLabel}
        </span>
        {snippet.isPermanent && (
          <span className="shrink-0 text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-wide border border-indigo-500/25 bg-indigo-500/10 text-indigo-400">
            Permanent
          </span>
        )}
        {isCloudBlocked && (
          <span className="shrink-0 text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-wide border border-red-500/30 bg-red-500/10 text-red-400" title="This snippet exceeds the Google Sheets cloud-safe character limit and is stored locally only.">
            Local only
          </span>
        )}
        <span className="ml-auto truncate text-[10px] font-semibold theme-muted">
          {usageCount > 0 ? `Used ${usageCount}×` : updatedLabel}
        </span>
        <button onClick={() => onTogglePin(snippet.id)} className={`snippet-icon-button w-7 h-7 flex items-center justify-center ${isPinned ? 'text-yellow-500' : ''}`} title={isPinned ? 'Unpin' : 'Pin'}>
          <SafeIcon icon={FiStar} className={isPinned ? 'fill-current' : ''} />
        </button>
      </div>

      <button onClick={() => onPreview(snippet)} className="text-left px-3 min-w-0">
        <h3 className="snippet-card-title text-[13px] md:text-sm font-bold leading-tight truncate" title={snippet.title}>
          {snippet.title || 'Untitled Snippet'}
        </h3>
      </button>

      <button onClick={() => onPreview(snippet)} className="text-left px-3 pt-2 min-w-0 flex-1 overflow-hidden">
        <p className="snippet-card-preview text-[11px] md:text-xs leading-[1.48] line-clamp-3 whitespace-pre-line break-words">
          {plainPreview || 'No preview available'}
        </p>
      </button>

      <div className="snippet-card-footer px-3 py-2 flex items-center gap-1.5 min-w-0">
        {isTemplate && (
          <span className="snippet-meta-chip text-[9px] px-1.5 py-0.5 rounded-md font-bold uppercase flex items-center gap-1">
            <SafeIcon icon={FiType} className="text-[8px]" /> Template
          </span>
        )}
        <span className="snippet-meta-chip text-[9px] px-1.5 py-0.5 rounded-md font-bold">{charCount} ch</span>
        {!snippet.isPermanent && (
          <span className={`hidden xl:flex items-center gap-1 text-[9px] font-semibold ${isExpiringSoon ? 'text-red-400' : 'theme-muted'}`}>
            <SafeIcon icon={isExpiringSoon ? FiAlertCircle : FiClock} className="text-[9px]" />
            {daysRemaining === 0 ? 'Purging' : `${daysRemaining}d`}
          </span>
        )}

        <div className="ml-auto flex items-center gap-0.5 shrink-0">
          <button onClick={() => onCopy(snippet, 'plain')} className="snippet-icon-button w-7 h-7 flex items-center justify-center" title="Copy"><SafeIcon icon={FiCopy} className="text-xs" /></button>
          {snippet.type === 'html' && (
            <button onClick={() => onCopy(snippet, 'html')} className="snippet-icon-button w-7 h-7 flex items-center justify-center" title="Copy rich text"><SafeIcon icon={FiCode} className="text-xs" /></button>
          )}
          <button onClick={() => onPreview(snippet)} className="snippet-icon-button w-7 h-7 flex items-center justify-center" title="Preview"><SafeIcon icon={FiEye} className="text-xs" /></button>
          <button onClick={() => onEdit(snippet)} className="snippet-icon-button w-7 h-7 flex items-center justify-center" title="Edit"><SafeIcon icon={FiEdit3} className="text-xs" /></button>
          <button onClick={() => onTogglePermanent(snippet.id)} className={`snippet-icon-button w-7 h-7 flex items-center justify-center ${snippet.isPermanent ? 'text-indigo-400' : ''}`} title="Toggle permanent"><SafeIcon icon={FiLock} className="text-xs" /></button>
          <button onClick={onDelete} className="snippet-icon-button w-7 h-7 flex items-center justify-center hover:!text-red-400" title="Move to Trash"><SafeIcon icon={FiTrash2} className="text-xs" /></button>
        </div>
      </div>
    </article>
  );
}